import React, { useState, useContext, useEffect, useMemo } from 'react';
import { AppointmentType, JobDescriptionType } from '../../../views/jobs/JobTypes';
import { FieldData, JobActivitiesFieldsetType,  } from 'types/FieldTypes';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'services/settings/SettingsContext';
import { useAllowedRight } from 'services/permissions/permissionChecks';
import FormFieldContext from '../FormFieldContext';
import TextareaInput from '../basefields/TextareaInput';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import '../../../style/scss/live-edit.scss';
import '../../../style/scss/forms.scss';

// Type to group the description blocks under an appointment or entry date
type DescriptionGroup = {
    dateKey: string;
    date: Date | null;
    blocks: JobDescriptionType[];
    hasAppointment: boolean;
};

const JobActivitiesFieldset: React.FC<JobActivitiesFieldsetType & { data: FieldData }> = ({
    name, data, isEditable
}) => {
    const { t } = useTranslation();
    const { userLocale, userTimezone, getTimeFormat } = useSettings();
    const hasRightCheck = useAllowedRight;
    const { editing, setUpdatedData } = useContext(FormFieldContext);
    const [rows, setRows] = useState<JobDescriptionType[]>([]);
    const [userHasOnlyViewRights] = useState<boolean>(hasRightCheck('only_view'));
    const appointments = data?.appointments || [];

    // Set the fetched descriptions in the rows state
    useEffect(() => {
        setRows(data?.[name] || []);
    }, [data, name]);

    // Group the visible description blocks by appointment date
    const groups = useMemo(() => {
        const groupsByDate: Record<string, DescriptionGroup> = {};
        const jobOnlyBlocks: JobDescriptionType[] = [];

        // Sort the blocks chronologically on their entered or created datetime
        const visibleBlocks = rows
            .filter(block => !block.deleted)
            .sort((a, b) => new Date(a.entered_at || a.created || 0).getTime() - new Date(b.entered_at || b.created || 0).getTime());

        // Determine the date key of a datetime as seen in the user timezone
        const getDateKey = (date: Date) => new Intl.DateTimeFormat('en-CA', {
            year: 'numeric', month: '2-digit', day: '2-digit', timeZone: userTimezone,
        }).format(date);

        // Add a block to the date group of the given date, creating the group when needed
        // Appointment groups and entry date groups are kept separate so their headers differ
        const addBlockToDateGroup = (block: JobDescriptionType, date: Date, isAppointmentDate: boolean) => {
            const dateKey = getDateKey(date);
            const groupKey = `${dateKey}-${isAppointmentDate ? 'appointment' : 'entry'}`;

            // Create the group for this date when it does not exist yet
            if (!groupsByDate[groupKey]) {
                groupsByDate[groupKey] = { dateKey, date, blocks: [], hasAppointment: isAppointmentDate };
            }

            // Add the block to the group when it is not added yet
            if (!groupsByDate[groupKey].blocks.includes(block)) {
                groupsByDate[groupKey].blocks.push(block);
            }
        };

        // Add every block to the group of each linked appointment date
        visibleBlocks.forEach(block => {
            const appointmentIds = block.appointments || [];

            // Track whether the block is added to at least one date group
            let addedToDateGroup = false;

            // Determine the unique appointment dates of the block
            appointmentIds.forEach(appointmentId => {
                const appointment = appointments.find((item: AppointmentType) => item.id === appointmentId);
                if (!appointment?.start_datetime) return;

                // Add the block to the group of the appointment date
                addBlockToDateGroup(block, new Date(appointment.start_datetime), true);

                // Mark the block as added to a date group
                addedToDateGroup = true;
            });

            // Group blocks without a resolvable appointment date on their entered or created date
            if (!addedToDateGroup) {
                const enteredDatetime = block.entered_at || block.created;
                if (enteredDatetime) {
                    addBlockToDateGroup(block, new Date(enteredDatetime), false);
                } else {
                    jobOnlyBlocks.push(block);
                }
            }
        });

        // Sort the date groups chronologically, showing the appointment group first on equal dates
        const dateGroups = Object.values(groupsByDate).sort((a, b) => {
            if (a.dateKey !== b.dateKey) return a.dateKey.localeCompare(b.dateKey);
            return Number(b.hasAppointment) - Number(a.hasAppointment);
        });

        // Add the blocks without any date as first group without date
        if (jobOnlyBlocks.length > 0) {
            dateGroups.unshift({ dateKey: '', date: null, blocks: jobOnlyBlocks, hasAppointment: false });
        }

        return dateGroups;
    }, [rows, appointments, userTimezone]);

    // Determine whether there are visible description blocks
    const hasVisibleBlocks = groups.length > 0;

    // Format the appointment date of a group header, only showing the year when it is not the current year
    const formatGroupDate = (date: Date) => {
        const currentYear = new Intl.DateTimeFormat('en-US', { year: 'numeric', timeZone: userTimezone }).format(new Date());
        const dateYear = new Intl.DateTimeFormat('en-US', { year: 'numeric', timeZone: userTimezone }).format(date);

        return new Intl.DateTimeFormat(userLocale, {
            day: 'numeric',
            month: 'long',
            ...(dateYear !== currentYear ? { year: 'numeric' } : {}),
            timeZone: userTimezone,
        }).format(date);
    };

    // Render the meta line of a block with the block type, creator and time
    const renderBlockMeta = (block: JobDescriptionType) => {
        // Get the type label of the block
        const typeLabel = t(`job.activities.type_${block.type || 'instruction'}`);

        // Format the time on which the block was entered or created
        const datetime = block.entered_at || block.created;
        const time = datetime ? new Intl.DateTimeFormat(userLocale, {
            hour: '2-digit',
            minute: '2-digit',
            hour12: getTimeFormat() === '12h',
            timeZone: userTimezone,
        }).format(new Date(datetime)) : '';

        // Combine the creator name and time behind the type label
        const nameAndTime = [block.created_by_user_name, time].filter(Boolean).join(' ');

        return (
            <div className='block-meta'>
                {nameAndTime ? `${typeLabel} - ${nameAndTime}` : typeLabel}
            </div>
        );
    };

    // Handle description change of a block
    const handleDescriptionChange = (value: string | React.ChangeEvent<HTMLTextAreaElement>, identifier?: string) => {
        // Get the new value from either the string or the change event
        const newValue = typeof value === 'string' ? value : value.target.value;

        // Copy the current rows and find its index
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);

        if (rowIndex !== -1) {
            // Set the new value in the specific row
            updatedRows[rowIndex] = { ...updatedRows[rowIndex], description: newValue };

            // Update the rows and updated data
            setRows(updatedRows);
            setUpdatedData((prev: any) => ({ ...prev, [name]: updatedRows }));
        };
    };

    // Handle deletion of a block
    const handleDeleteRow = (identifier: string) => {
        const updatedRows = [...rows];
        const rowIndex = updatedRows.findIndex(row => row.id?.toString() === identifier || row.uuid === identifier);

        if (rowIndex !== -1) {
            // Mark the block as deleted without actually deleting it
            updatedRows[rowIndex].deleted = true;
            setRows(updatedRows);
            setUpdatedData((prev: any) => ({ ...prev, [name]: updatedRows }));
        }
    };

    // Render the grouped description blocks for the edit or view mode
    const renderGroups = (mode: 'edit' | 'view') => (
        groups.map(group => (
            <div key={group.dateKey ? `${group.dateKey}-${group.hasAppointment ? 'appointment' : 'entry'}` : 'job-only'} className='appointment-group'>
                {group.date && (
                    <div className='appointment-group-header'>
                        {group.hasAppointment ? (
                            t('job.activities.appointment_group_header', { date: formatGroupDate(group.date) })
                        ) : (
                            formatGroupDate(group.date)
                        )}
                    </div>
                )}
                {group.blocks.map(block => {
                    const identifier = block.id?.toString() || block.uuid || '';
                    return (
                        <div key={`${group.dateKey}-${identifier}`} className='description-block'>
                            {mode === 'edit' ? (
                                <>
                                    <div className='description-block-row'>
                                        <TextareaInput
                                            name='description'
                                            value={block.description}
                                            rows={3}
                                            placeholder={t('job.activities.description_placeholder')}
                                            onChange={(value) => handleDescriptionChange(value, identifier)}
                                        />
                                        <div className='delete-icon tooltip-icon'>
                                            <FontAwesomeIcon
                                                icon={faTrash}
                                                onClick={() => handleDeleteRow(identifier)} />
                                            <span className="tooltip">{t('general.delete')}</span>
                                        </div>
                                    </div>
                                    {renderBlockMeta(block)}
                                </>
                            ) : (
                                <div className='description-text'>
                                    {block.description}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        ))
    );

    return (
        <div className='rows-field job-activities-field'>
            {editing ? (
                // Edit mode
                <div className='edit-mode'>
                    {renderGroups('edit')}
                </div>
            ) : (
                // View mode
                <div className={`full-width-alignment ${hasVisibleBlocks && isEditable?.editability && !userHasOnlyViewRights ? 'editable' : ''}`}>
                    <div className="view-mode">
                        <span className='p'>
                            {hasVisibleBlocks ? (
                                renderGroups('view')
                            ) : (
                                <span className='empty-value'>
                                    {t('job.activities.no_descriptions_label')}
                                </span>
                            )}
                        </span>
                        {hasVisibleBlocks && (
                            <span className='edit-icon'>
                                <FontAwesomeIcon icon={faPen} />
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default JobActivitiesFieldset;
